import { UserIcon, UsersIcon, type LucideIcon } from 'lucide-react';
import { type JSX, useState } from 'react';

import { Button } from '@/components/ui/button.js';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command.js';
import { Input } from '@/components/ui/input.js';
import {
  groupMemberCountLabel,
  rememberGroupName,
  useDirectoryGroupSearch,
} from '@/lib/api/directory-groups.js';
import { useUserSearch } from '@/lib/api/users.js';
import { titleWhenTruncated } from '@/lib/title-when-truncated.js';

/**
 * One addressee of a `named` share — a colleague, or an Entra group every member of which may take a
 * copy. It carries the wire's own `subject_type` values so nothing has to be translated on the way out,
 * and a group carries the size it was picked at: the chip is the last place that number is visible, and
 * re-reading it would be a directory call per render.
 */
export type ShareRecipient =
  | { subjectType: 'user'; subjectId: string; label: string }
  | {
      subjectType: 'entra_group';
      subjectId: string;
      label: string;
      memberCount: number | null;
    };

/** One offered row, in the shape the popup's own `@` picker uses — see `composer/mention-entry.ts`. */
interface PickerRow {
  key: string;
  icon: LucideIcon;
  label: string;
  detail: string;
  /**
   * What a screen reader announces. It names the KIND, because cmdk marks a group heading `aria-hidden`
   * — the heading tells the two directories apart by eye only.
   */
  accessibleName: string;
  recipient: ShareRecipient;
}

/** The rows of one directory, under the heading they are listed by. */
interface PickerSection {
  key: string;
  heading: string;
  rows: PickerRow[];
}

/**
 * Who to hand the file to: one search box over both directories, results under a heading each.
 *
 * It was two tabs, `People` and `Groups`. That worked while the dialog had no tab row of its own, and
 * stopped working the moment it got one — two stacked tablists is exactly the clutter this screen is
 * being cured of. So the shape is the composer's `@` picker instead: one query, one list, a section per
 * source, and a source that found nothing contributes no heading.
 *
 * The one thing merging costs, and the one thing that is paid back here explicitly: the two directories
 * fail independently. Users come from the platform's own identity service; groups come from Microsoft
 * Graph, which answers 502 whenever it is unreachable — which, on a local stand, is always. A single box
 * makes it very easy for that outage to read as "nothing found", so the group failure gets its own line
 * and its own retry, and the people it DID find are listed regardless. An empty picker would be a lie
 * about the directory that answered.
 */
export function ArtifactRecipientPicker({
  recipients,
  onPick,
}: {
  recipients: readonly ShareRecipient[];
  onPick: (recipient: ShareRecipient) => void;
}): JSX.Element {
  const [term, setTerm] = useState('');
  const peopleQuery = useUserSearch(term);
  const groupsQuery = useDirectoryGroupSearch(term);
  const searched = term.trim().length > 0;
  const waitingForPeople =
    peopleQuery.isLoading ||
    (peopleQuery.data === undefined && !peopleQuery.isError);
  const waitingForGroups =
    groupsQuery.isLoading ||
    (groupsQuery.data === undefined && !groupsQuery.isError);

  /**
   * Somebody already on the share is not offered again — picking them twice means nothing. User ids and
   * group object ids are separate spaces, so each side filters against its own kind.
   */
  const pickedIds = (
    kind: ShareRecipient['subjectType'],
  ): ReadonlySet<string> =>
    new Set(
      recipients
        .filter((recipient) => recipient.subjectType === kind)
        .map((recipient) => recipient.subjectId),
    );

  const pickedUserIds = pickedIds('user');
  const pickedGroupIds = pickedIds('entra_group');

  const peopleRows: PickerRow[] = (peopleQuery.data ?? [])
    .filter((user) => !pickedUserIds.has(user.id))
    .map((user) => {
      const label = user.name ?? user.email;
      return {
        key: `user:${user.id}`,
        icon: UserIcon,
        label,
        detail: user.email,
        accessibleName: `Person ${label}, ${user.email}`,
        recipient: { subjectType: 'user', subjectId: user.id, label },
      };
    });

  const groupRows: PickerRow[] = (groupsQuery.data ?? [])
    .filter((group) => !pickedGroupIds.has(group.id))
    .map((group) => {
      const size = groupMemberCountLabel(group.memberCount);
      return {
        key: `group:${group.id}`,
        icon: UsersIcon,
        label: group.name,
        detail: size,
        accessibleName: `Group ${group.name} · ${size}`,
        recipient: {
          subjectType: 'entra_group',
          subjectId: group.id,
          label: group.name,
          memberCount: group.memberCount,
        },
      };
    });

  const sections: PickerSection[] = [
    { key: 'people', heading: 'People', rows: peopleRows },
    { key: 'groups', heading: 'Groups', rows: groupRows },
  ].filter((section) => section.rows.length > 0);

  function take(row: PickerRow): void {
    if (row.recipient.subjectType === 'entra_group') {
      rememberGroupName({
        id: row.recipient.subjectId,
        name: row.recipient.label,
        memberCount: row.recipient.memberCount,
      });
    }
    onPick(row.recipient);
    setTerm('');
  }

  return (
    <div className="flex flex-col gap-2">
      <Input
        aria-label="Search people and groups"
        value={term}
        placeholder="Search people and groups"
        autoComplete="off"
        onChange={(event) => setTerm(event.target.value)}
      />

      {searched && (
        <>
          <Command shouldFilter={false} className="bg-transparent">
            <CommandList className="max-h-40">
              {sections.length === 0 ? (
                <CommandEmpty>
                  {waitingForPeople || waitingForGroups
                    ? 'Searching…'
                    : 'No matching people or groups.'}
                </CommandEmpty>
              ) : (
                sections.map((section) => (
                  <CommandGroup key={section.key} heading={section.heading}>
                    {section.rows.map((row) => (
                      <CommandItem
                        key={row.key}
                        value={row.key}
                        aria-label={row.accessibleName}
                        onSelect={() => take(row)}
                      >
                        <row.icon
                          className="size-4 shrink-0 text-muted-foreground"
                          aria-hidden
                        />
                        <div className="flex min-w-0 flex-col">
                          <span
                            className="truncate"
                            onMouseEnter={titleWhenTruncated}
                          >
                            {row.label}
                          </span>
                          <span
                            className="truncate text-xs text-muted-foreground"
                            onMouseEnter={titleWhenTruncated}
                          >
                            {row.detail}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))
              )}
            </CommandList>
          </Command>

          {groupsQuery.isError && (
            <div className="flex items-center gap-2">
              <p className="text-xs text-destructive">
                Couldn&apos;t reach the company directory, so groups are not
                listed.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void groupsQuery.refetch()}
              >
                Retry
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
